import { useValueObserver } from '@superdispatch/hooks';
import { useMemo } from 'react';
import { APIPageResponse } from 'shared/api/CarrierAPIClient';
import { useCarrierFeatures } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { PostingDTO } from '../../data/dto/PostingDTO';
import { PostingSavedSearchDTO } from '../../data/dto/PostingSavedSearchDTO';
import { isEmptyCriteria } from '../../data/dto/PostingsPageParamsDTO';
import {
  getPostingListIdentity,
  usePostingList,
} from '../../data/LoadboardPostingAPI';
import { useSavedSearches } from '../../data/LoadboardSavedSearchesAPI';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import { usePostingsPageContext } from './PostingsPageContext';

export interface PostingsPageState {
  postings: PostingDTO[] | undefined;
  postingsPageError: Error | null;
  isPostingsPageLoading?: boolean;
  isPostingsPageFetching?: boolean;
  isPostingsPageSettled?: boolean;
  postingsPagination?: APIPageResponse['pagination'];
}

export function useCurrentSavedSearch(): PostingSavedSearchDTO | undefined {
  const savedSearches = useSavedSearches();
  const { params } = usePostingsPageContext();

  return useMemo(
    () =>
      savedSearches.data?.find(
        (item) => item.guid === params.current_saved_search_guid,
      ),
    [savedSearches, params.current_saved_search_guid],
  );
}

export function usePostingsListScrollKey(
  postings: PostingDTO[] | undefined,
): string {
  const isScrollPositionEnabled = useFlag('slb_load_list_scroll_position');
  const { params } = usePostingsPageContext();

  const searchKey = useMemo(
    () =>
      JSON.stringify([
        getPostingListIdentity(params),
        params.current_saved_search_guid,
      ]),
    [params],
  );

  const postingGuids = useMemo(
    () => postings?.map((posting) => posting.load.guid).join(',') ?? '',
    [postings],
  );

  return isScrollPositionEnabled ? searchKey : postingGuids;
}

export function usePostingsPageState(): PostingsPageState {
  const features = useCarrierFeatures();
  const { params } = usePostingsPageContext();
  const postings = usePostingList(params);

  useValueObserver(postings.isFetched, () => {
    if (postings.isFetched && postings.data && !isEmptyCriteria(params)) {
      trackLoadboardEvent({
        params,
        features,
        name: 'SLB: Searched For Loads',
        criteria: params.search_criteria,
        resultCount: postings.data.pagination.count,
      });
    }
  });

  return {
    postings: postings.data?.data,
    postingsPagination: postings.data?.pagination,

    postingsPageError: postings.error,
    isPostingsPageLoading: postings.isLoading,
    isPostingsPageFetching: postings.isFetching,
    isPostingsPageSettled: !postings.isPlaceholderData && !postings.isFetching,
  };
}
